import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import { saveAs } from "file-saver";
import {
  useCallback,
  useRef,
  useState,
  type ChangeEvent,
  type FunctionComponent,
} from "react";
import type { ExportImageType } from "../features/GraphExporter";
import type GraphContainerState from "../states/GraphContainerState";
import CollapsiblePanel from "./CollapsiblePanel";

interface SaveLoadPanelProps {
  onSave: () => GraphContainerState;
  onLoad: (graphContainerState: GraphContainerState) => void;
  hasNodes: boolean;
  onNewGraph: () => void;
  onExport: (imageType: ExportImageType) => void;
  isExporting: boolean;
}

const SaveLoadPanel: FunctionComponent<SaveLoadPanelProps> = ({
  onSave,
  onLoad,
  hasNodes,
  onNewGraph,
  onExport,
  isExporting,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const [isLoadingSuccess, setLoadingSuccess] = useState(false);
  const [isLoadingError, setLoadingError] = useState(false);
  const [isConfirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const handleSave = useCallback(() => {
    const data = onSave();
    const serializedData = JSON.stringify(data, null, 4);
    const blob = new Blob([serializedData], {
      type: "text/plain;charset=utf-8",
    });
    saveAs(blob, "graph.json");
  }, [onSave]);

  const handleLoad = useCallback(() => {
    if (inputRef.current === null) {
      return;
    }
    inputRef.current.click();
  }, []);

  const handleNewGraphClick = useCallback(() => {
    if (hasNodes) {
      setConfirmDialogOpen(true);
    } else {
      onNewGraph();
    }
  }, [hasNodes, onNewGraph]);

  const handleConfirmNewGraph = useCallback(() => {
    setConfirmDialogOpen(false);
    onNewGraph();
  }, [onNewGraph]);

  const handleCancelNewGraph = useCallback(() => {
    setConfirmDialogOpen(false);
  }, []);

  const loadFile = useCallback(
    (file: File) => {
      const reader = new FileReader();

      reader.onload = () => {
        const serializedData = reader.result;
        if (typeof serializedData !== "string") {
          return;
        }

        try {
          const data = JSON.parse(serializedData) as GraphContainerState;
          setLoadingSuccess(true);
          setLoadingError(false);
          onLoad(data);
        } catch (error) {
          console.error(error);
          setLoadingSuccess(false);
          setLoadingError(true);
        }
      };

      reader.onerror = () => {
        console.error(reader.error);
        setLoadingSuccess(false);
        setLoadingError(true);
      };

      reader.readAsText(file);
    },
    [onLoad],
  );

  const handleFileChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (files == null || files.length === 0) {
        return;
      }
      const file = files[0];
      loadFile(file);
    },
    [loadFile],
  );

  return (
    <>
      <CollapsiblePanel title="Save/Load & Export">
        {/* Contents */}
        <Stack px={2} py={1} spacing={2.5}>
          {/* New Blank Graph Section */}
          <section>
            <Typography variant="subtitle2" gutterBottom>
              New Graph
            </Typography>
            <Button
              fullWidth
              variant="outlined"
              color="primary"
              onClick={handleNewGraphClick}
            >
              New Blank Graph
            </Button>
          </section>

          <Divider />

          {/* Save / Load Section */}
          <section>
            <Typography variant="subtitle2" gutterBottom>
              Save / Load (JSON)
            </Typography>
            <Stack spacing={1.5}>
              <Button
                variant="contained"
                onClick={() => {
                  handleSave();
                }}
              >
                Save
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  handleLoad();
                }}
              >
                Load
              </Button>
            </Stack>
          </section>

          <Divider />

          {/* Export Section */}
          <section>
            <Typography variant="subtitle2" gutterBottom>
              Export as Image
            </Typography>
            <Stack spacing={1.5}>
              <Button
                variant="outlined"
                disabled={isExporting}
                onClick={() => {
                  onExport("png");
                }}
              >
                Export as PNG
              </Button>
              <Button
                variant="outlined"
                disabled={isExporting}
                onClick={() => {
                  onExport("svg");
                }}
              >
                Export as SVG
              </Button>
            </Stack>
          </section>

          <Divider />

          {/* Help text */}
          <section>
            <Typography variant="subtitle2" gutterBottom>
              What Will Be Saved/Loaded
            </Typography>
            <List
              dense
              disablePadding
              component="ul"
              sx={{ listStyle: "disc", pl: 3 }}
            >
              <ListItem disablePadding sx={{ display: "list-item" }}>
                <ListItemText primary="Nodes and connections" />
              </ListItem>
              <ListItem disablePadding sx={{ display: "list-item" }}>
                <ListItemText primary="Operations" />
              </ListItem>
              <ListItem disablePadding sx={{ display: "list-item" }}>
                <ListItemText primary="Toolbar status" />
              </ListItem>
            </List>
          </section>

          {/* Successful message for loading */}
          {isLoadingSuccess && (
            <Alert severity="success">
              The file has been loaded successfully.
            </Alert>
          )}

          {/* Error message for loading */}
          {isLoadingError && (
            <Alert severity="error">
              An error has occurred while loading the file. See the developer
              console for details.
            </Alert>
          )}
        </Stack>
      </CollapsiblePanel>

      {/* Confirmation Dialog for New Blank Graph */}
      <Dialog
        open={isConfirmDialogOpen}
        onClose={handleCancelNewGraph}
        aria-labelledby="new-graph-dialog-title"
        aria-describedby="new-graph-dialog-description"
      >
        <DialogTitle id="new-graph-dialog-title">
          Create New Blank Graph?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="new-graph-dialog-description">
            Are you sure you want to create a new blank graph? Any unsaved
            changes will be lost.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelNewGraph}>Cancel</Button>
          <Button
            onClick={handleConfirmNewGraph}
            variant="contained"
            color="primary"
            autoFocus
          >
            Create New Graph
          </Button>
        </DialogActions>
      </Dialog>

      {/* Input to load file */}
      <input
        aria-label="Load file"
        style={{ display: "none" }}
        ref={inputRef}
        type="file"
        onChange={handleFileChange}
        value="" // avoid no change on second click with the same file
      />
    </>
  );
};

export default SaveLoadPanel;
